import React, { useState } from 'react';
import { X, Loader2, Wand2, Copy, FileText, Download, ExternalLink } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { toast } from '../lib/toast';
import { jsPDF } from 'jspdf';
import { supabase } from '../lib/supabase';

interface SavedOutput {
  id: string;
  saved: boolean;
}

interface MeetingAgendaPlannerProps {
  onClose: () => void;
}

export default function MeetingAgendaPlanner({ onClose }: MeetingAgendaPlannerProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [savedOutput, setSavedOutput] = useState<SavedOutput | null>(null);
  const [savingOutput, setSavingOutput] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      toast.error('Please enter meeting details');
      return;
    }

    setLoading(true);
    try {
      const prompt = `Create a detailed meeting agenda based on the following information:

${input}

Format the response as follows:

📅 Meeting Title
📆 Date: [Date]
⏰ Time: [Time]
📍 Location: [Location]
👥 Attendees: [Attendees]

1️⃣ Welcome & Meeting Objectives (5 min)
[Brief description]

Discussion Questions:
- [Question 1]
- [Question 2]

2️⃣ [Agenda Item 2] (XX min)
[Brief description]

Discussion Questions:
- [Question 1]
- [Question 2]

[Continue with additional agenda items as needed]

Final agenda item should be Action Items & Next Steps, followed by Open Discussion & Closing Remarks.

Make sure each agenda item includes:
1. A clear title with emoji and time allocation
2. A brief description of what will be discussed
3. 2-4 specific discussion questions to guide the conversation
4. Logical flow from one topic to the next

Use the information provided to create a comprehensive and well-structured agenda that addresses all the mentioned topics and concerns.`;

      const response = await generateTaskDescription(prompt, 'task');
      setResult(response);

      // Prepare for saving but don't save automatically
      setSavedOutput({
        id: crypto.randomUUID(),
        saved: false
      });
    } catch (error) {
      console.error('Error generating agenda:', error);
      toast.error('Failed to generate meeting agenda');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!result || !savedOutput || savedOutput.saved) return;
    
    setSavingOutput(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save outputs');
        setSavingOutput(false);
        return;
      }
      
      // First try to find the agent by ID
      const { data: existingAgent, error: fetchError } = await supabase
        .from('ai_agents')
        .select('id')
        .eq('id', '00000000-0000-0000-0000-000000000004')
        .limit(1)
        .maybeSingle();
      
      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Error fetching agent:', fetchError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      let agentId: string;
      
      if (!existingAgent) {
        // Try to find by name
        const { data: agentByName, error: nameError } = await supabase
          .from('ai_agents')
          .select('id')
          .eq('name', 'Meeting Agenda Planner')
          .limit(1)
          .maybeSingle();
          
        if (nameError && nameError.code !== 'PGRST116') {
          console.error('Error finding agent by name:', nameError);
          toast.error('Failed to save output');
          setSavingOutput(false);
          return;
        }
        
        if (agentByName) {
          agentId = agentByName.id;
        } else {
          // Create new agent
          const { data: newAgent, error: createError } = await supabase
            .from('ai_agents')
            .insert({
              name: 'Meeting Agenda Planner',
              user_id: user.id,
              goals: 'Generate structured meeting agendas with discussion questions',
              system_prompt: 'You are an expert meeting planner. Create detailed meeting agendas with discussion questions for each topic.',
              is_custom: false,
              agent_type: 'text'
            })
            .select('id')
            .single();
            
          if (createError) {
            console.error('Error creating agent:', createError);
            toast.error('Failed to create agent');
            setSavingOutput(false);
            return;
          }
          
          agentId = newAgent.id;
        }
      } else {
        agentId = existingAgent.id;
      }
      
      // Save output to ai_agent_outputs table
      const { error: insertError } = await supabase.from('ai_agent_outputs').insert({
        user_id: user.id,
        agent_id: agentId,
        prompt: input,
        output_url: result,
        generation_type: 'text',
        llm_model: 'gpt-3.5-turbo',
        metadata: {},
        agent_type: 'text'
      });

      if (insertError) {
        console.error('Error saving output:', insertError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      toast.success('Output saved successfully');
      setSavedOutput({...savedOutput, saved: true});
    } catch (error) {
      console.error('Error saving agent output:', error);
      toast.error('Failed to save output');
    } finally {
      setSavingOutput(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result || '');
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDownload = () => {
    try {
      // Strip HTML tags and decode HTML entities
      const stripHtml = (html: string) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
      };

      const content = stripHtml(result || '');
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'meeting-agenda.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading agenda:', error);
      toast.error('Failed to download agenda');
    }
  };

  const downloadPDF = () => {
    try {
      if (!result) return;
      
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      const lineHeight = 8;
      
      // Add header with title
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add title with white text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = 'Meeting Agenda';
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Parse HTML content
      const parser = new DOMParser();
      const doc = parser.parseFromString(result, 'text/html');
      
      // Start position after header
      let y = 50;
      
      // Function to clean and replace emoji characters with text equivalents
      const cleanText = (text: string): string => {
        // First replace common emoji with text equivalents
        let cleanedText = text
          .replace(/1️⃣|1⃣/g, "1. ")
          .replace(/2️⃣|2⃣/g, "2. ")
          .replace(/3️⃣|3⃣/g, "3. ")
          .replace(/4️⃣|4⃣/g, "4. ")
          .replace(/5️⃣|5⃣/g, "5. ")
          .replace(/6️⃣|6⃣/g, "6. ")
          .replace(/7️⃣|7⃣/g, "7. ")
          .replace(/8️⃣|8⃣/g, "8. ")
          .replace(/9️⃣|9⃣/g, "9. ")
          .replace(/0️⃣|0⃣/g, "0. ")
          .replace(/📅|📆/g, "Date: ")
          .replace(/⏰/g, "Time: ")
          .replace(/📍/g, "Location: ")
          .replace(/👥/g, "Attendees: ");
          
        // Then remove any remaining emoji or special characters
        cleanedText = cleanedText.replace(/[\u{1F300}-\u{1F6FF}]/gu, "");
        
        return cleanedText.trim();
      };
      
      // Process each element
      const processNode = (node: Element, indent = 0) => {
        const text = node.textContent?.trim() || '';
        if (!text) return;
        
        // Clean and replace emojis with text equivalents
        const processedText = cleanText(text);
        if (!processedText) return;
        
        // Check if we need a new page
        if (y > pdf.internal.pageSize.height - margin) {
          pdf.addPage();
          y = margin;
        }
        
        // Handle different node types
        if (node.tagName === 'H1' || node.tagName === 'H2' || node.tagName === 'H3') {
          // Section headers
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          
          // Add background for headers
          if (processedText.includes('Meeting') || processedText.match(/[0-9]/)) {
            pdf.setFillColor(240, 247, 255); // Light blue background
            pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
          }
          
          pdf.text(processedText, margin + indent, y);
          y += lineHeight + 4;
        } else if (node.tagName === 'P') {
          // Regular paragraphs
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
          
          // Handle multi-line text
          const wrappedText = pdf.splitTextToSize(processedText, pageWidth - (margin * 2) - indent);
          if (wrappedText.length > 0) {
            pdf.text(wrappedText, margin + indent, y);
          }
          y += wrappedText.length * lineHeight;
        } else if (node.tagName === 'UL' || node.tagName === 'OL') {
          // Lists are handled by processing their children
          y += 2;
        } else if (node.tagName === 'LI') {
          // List items
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
          
          // Add bullet point
          pdf.text('•', margin + indent, y);
          
          // Wrap text with proper indentation
          const wrappedText = pdf.splitTextToSize(processedText, pageWidth - (margin * 2) - indent - 10);
          if (wrappedText.length > 0) {
            pdf.text(wrappedText, margin + indent + 5, y);
          }
          y += wrappedText.length * lineHeight;
        } else if (node.tagName === 'STRONG' || node.tagName === 'B') {
          // Bold text
          pdf.setFont('helvetica', 'bold');
          pdf.text(processedText, margin + indent, y);
          y += lineHeight;
          pdf.setFont('helvetica', 'normal');
        } else if (text.includes('Discussion Questions:')) {
          // Discussion questions header
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(79, 70, 229); // Indigo color
          pdf.text('Discussion Questions:', margin + indent, y);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont('helvetica', 'normal');
          y += lineHeight + 2;
        } else {
          // Default text handling
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
          const wrappedText = pdf.splitTextToSize(processedText, pageWidth - (margin * 2) - indent);
          if (wrappedText.length > 0) {
            pdf.text(wrappedText, margin + indent, y);
          }
          y += wrappedText.length * lineHeight;
        }
        
        // Process children with increased indent
        Array.from(node.children).forEach(child => {
          processNode(child as Element, indent + 5);
        });
      };
      
      // Add original input section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Meeting Details:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const wrappedInput = pdf.splitTextToSize(input, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * lineHeight + 15;
      
      // Check if we need a new page
      if (y > pdf.internal.pageSize.height - 40) {
        pdf.addPage();
        y = 20;
      }
      
      // Add agenda section header
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255); // Light blue background
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Meeting Agenda:', margin, y);
      y += 15;
      
      // Process all elements
      Array.from(doc.body.children).forEach(node => {
        processNode(node as Element);
      });
      
      // Add footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated on ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`, margin, pdf.internal.pageSize.height - 10);
      }
      
      pdf.save('meeting-agenda.pdf');
    } catch (error) {
      console.error('Error creating PDF:', error);
      toast.error('Failed to create PDF');
    }
  };

  const handleDownloadTemplate = () => {
    try {
      // Create a PDF template instead of text
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      
      // Add header
      pdf.setFillColor(79, 70, 229);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Meeting Agenda Template', margin, 25);
      
      // Reset text color
      pdf.setTextColor(0, 0, 0);
      
      let y = 50;
      
      // Example section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Example User Input:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const exampleInput = 
"Hey AI, we have a project check-in meeting tomorrow at 3 PM. We need to discuss where we stand with the new website launch. The dev team has faced some API integration issues, and the client wants an update on the homepage design. Also, we should finalize the content strategy. Oh, and marketing wants to talk about the social media campaign. Can you create an agenda?"
      
      const wrappedInput = pdf.splitTextToSize(exampleInput, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 8 + 15;
      
      // Example output section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Example Output:', margin, y);
      y += 10;
      
      // Full example output
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Project Check-In Meeting Agenda', margin, y);
      y += 10;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Date: [Tomorrow\'s Date]', margin, y);
      y += 6;
      pdf.text('Time: 3:00 PM', margin, y);
      y += 6;
      pdf.text('Location: [Meeting Room / Zoom Link]', margin, y);
      y += 6;
      pdf.text('Attendees: Project Team, Developers, Designers, Marketing, Client Representative', margin, y);
      y += 12;
      
      // Agenda items
      const agendaItems = [
        {
          title: '1. Welcome & Meeting Objectives (5 min)',
          desc: 'Purpose of the meeting\nKey discussion points and expected outcomes',
          questions: [
            'What are the top priorities for today\'s meeting?',
            'Are there any additional topics that should be covered?'
          ]
        },
        {
          title: '2. Website Launch Progress Update (10 min)',
          desc: 'Current development status\nRemaining tasks and expected timeline',
          questions: [
            'What milestones have we achieved so far?',
            'Are we still on track for the launch date? If not, what are the causes of delays?',
            'Are there any dependencies blocking progress?'
          ]
        },
        {
          title: '3. API Integration Issues (15 min)',
          desc: 'Technical challenges faced by the development team\nPossible solutions and required support',
          questions: [
            'What specific issues are being faced with API integration?',
            'Has the root cause of these issues been identified?',
            'What solutions have been attempted so far?',
            'Are additional resources or external support needed?'
          ]
        },
        {
          title: '4. Homepage Design Feedback (10 min)',
          desc: 'Review client feedback\nDiscuss required changes and finalization timeline',
          questions: [
            'What were the key points from the client\'s feedback?',
            'Are there any conflicting design preferences?',
            'What changes need to be made, and how will they impact the timeline?'
          ]
        },
        {
          title: '5. Content Strategy Finalization (10 min)',
          desc: 'Review current content progress\nAlign messaging with website goals',
          questions: [
            'Has the key messaging and tone of voice been finalized?',
            'Is the content aligned with the website\'s design and structure?',
            'Are there any gaps that need to be addressed?',
            'What is the approval process for finalizing the content?'
          ]
        },
        {
          title: '6. Marketing & Social Media Campaign (10 min)',
          desc: 'Updates from the marketing team\nStrategy and coordination with the content team',
          questions: [
            'What is the overall goal of the social media campaign?',
            'Has the content calendar been aligned with the website launch?',
            'Are additional assets such as graphics, videos, or blogs needed?',
            'What key performance indicators (KPIs) will be used to measure campaign success?'
          ]
        },
        {
          title: '7. Action Items & Next Steps (10 min)',
          desc: 'Assigning responsibilities for pending tasks\nSetting deadlines and expectations',
          questions: [
            'What are the top three priorities before the next meeting?',
            'Who is responsible for each action item?',
            'What are the deadlines for pending tasks?',
            'Are there any potential risks that might delay execution?'
          ]
        },
        {
          title: '8. Open Discussion & Closing Remarks (5 min)',
          desc: 'Addressing any additional concerns\nConfirming the next meeting date and follow-ups',
          questions: [
            'Does anyone have additional concerns or updates?',
            'Are there any blockers that have not been addressed?',
            'When should the next check-in meeting be scheduled?'
          ]
        }
      ];
      
      // Add all agenda items
      agendaItems.forEach(item => {
        // Check if we need a new page
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = margin;
        }
        
        // Item title with background
        pdf.setFillColor(240, 247, 255);
        pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text(item.title, margin, y);
        y += 12;
        
        // Item description
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const descLines = item.desc.split('\n');
        descLines.forEach(line => {
          pdf.text(line, margin, y);
          y += 6;
        });
        y += 3;
        
        // Discussion questions
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(79, 70, 229);
        pdf.text('Discussion Questions:', margin, y);
        y += 7;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        item.questions.forEach(question => {
          pdf.text('• ' + question, margin + 5, y);
          y += 6;
        });
        
        y += 8;
      });
      
      // Add footer
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text('Generated by PM Pro AI', margin, pdf.internal.pageSize.height - 10);

      pdf.save('meeting-agenda-template.pdf');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const toggleFullResponse = () => {
    setShowFullResponse(!showFullResponse);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Meeting Agenda Planner
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">Instructions</h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-4">
              Describe your upcoming meeting details including purpose, topics to discuss, attendees, and any specific concerns. The AI will generate a structured agenda with discussion questions for each topic.
            </p>
            <div className="flex space-x-2">
              <button
                onClick={handleDownloadTemplate}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download Example PDF
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="meeting-details" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Meeting Details
              </label>
              <textarea
                id="meeting-details"
                rows={6}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full h-24 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Describe your meeting details here..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    Generating Agenda...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    Generate Agenda
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Meeting Agenda
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    Copy
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Download PDF
                  </button>
                  <button
                    onClick={handleSaveOutput}
                    disabled={savingOutput || (savedOutput && savedOutput.saved)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {savingOutput ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-1.5" />
                        Saving...
                      </>
                    ) : savedOutput && savedOutput.saved ? (
                      'Saved'
                    ) : (
                      'Save Output'
                    )}
                  </button>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mb-4">
                <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                  Meeting Agenda Generated Successfully
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}